import React from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { Breadcrumb, Row, Col, Button, Space, Card } from 'antd';
import { InfoCircleOutlined, PartitionOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import { PageRoutes } from '../../conf/Global';
import { useEntityRegistry } from '../useEntityRegistry';
import { EntityType } from '../../types.generated';
import { navigateToLineageUrl } from '../lineage/utils/navigateToLineageUrl';
import useIsLineageMode from '../lineage/utils/useIsLineageMode';
import { ReactComponent as ChangeLogIcon } from '../../images/icon-change-log.svg';
import useIsChangeLogMode from '../entity/shared/containers/profile/utils/useIsChangeLogMode';
import { SLASH_DECODE_KEY } from '../entity/shared/constants';
import utilService from '../../utils/util.service';
import { useTranslation } from '../../i18n/useTranslation';

const BrowsePathCard = styled(Card)`
    margin-top: 0px;
`;

interface Props {
    type: EntityType;
    path: Array<string>;
    lineageSupported?: boolean;
    isProfilePage?: boolean;
    isBrowsable?: boolean;
    isLoading?: boolean;
}

/**
 * Responsible for rendering a clickable browse path view.
 */
export const LegacyBrowsePath = ({ type, path, lineageSupported, isProfilePage, isBrowsable, isLoading }: Props) => {
    const entityRegistry = useEntityRegistry();
    const history = useHistory();
    const location = useLocation();
    const isLineageMode = useIsLineageMode();
    const isChangeLogMode = useIsChangeLogMode();
    const { t } = useTranslation();

    const createPartialPath = (parts: Array<string>) => {
        const pathArr = [...parts];

        pathArr.forEach((part, i) => {
            if (part.includes('/')) {
                pathArr[i] = part.replaceAll('/', SLASH_DECODE_KEY);
            }
        });

        const partialPath = pathArr.join('/');

        return utilService.encodeSpecialChars(partialPath);
    };

    const baseBrowsePath = `${PageRoutes.BROWSE}/${entityRegistry.getPathName(type)}`;

    // Decode special characters in the path
    const displayPathCrumb = (part) => {
        return part?.replaceAll(SLASH_DECODE_KEY, '/');
    };

    const pathCrumbs = path.map((part, index) => (
        <Breadcrumb.Item key={`${part || index}`}>
            <Link
                to={
                    (isProfilePage && index === path.length - 1) || !isBrowsable
                        ? '#'
                        : `${baseBrowsePath}/${createPartialPath(path.slice(0, index + 1))}`
                }
            >
                {displayPathCrumb(part)}
            </Link>
        </Breadcrumb.Item>
    ));

    return (
        <>
            <BrowsePathCard className={`px-3 py-2 ${isLoading && 'mb-0'}`}>
                <Breadcrumb>
                    <Breadcrumb.Item>
                        <Link to={isBrowsable ? baseBrowsePath : '#'}>{t(entityRegistry.getCollectionName(type))}</Link>
                    </Breadcrumb.Item>
                    {pathCrumbs}
                </Breadcrumb>
            </BrowsePathCard>
            {lineageSupported && (
                <Card className="f-bottom-border px-3 py-2">
                    <Row align="middle" justify="end">
                        <Col flex="none" className="f-mt-3-xs">
                            <Space align="center" size={0}>
                                <Button
                                    type="text"
                                    aria-label={t('Change Log')}
                                    className={`${isChangeLogMode ? 'f-btn-active' : ''} f-lineage-btn f-btn-flat`}
                                    onClick={() =>
                                        navigateToLineageUrl({
                                            location,
                                            history,
                                            isLineageMode: false,
                                            isChangeLogMode: true,
                                        })
                                    }
                                >
                                    <ChangeLogIcon
                                        style={{
                                            verticalAlign: 'middle',
                                        }}
                                    />
                                    <span>{t('Change Log')}</span>
                                </Button>
                                <Button
                                    type="text"
                                    aria-label={t('Details')}
                                    className={`${!isLineageMode ? 'f-btn-active' : ''} f-lineage-btn f-btn-flat`}
                                    onClick={() => navigateToLineageUrl({ location, history, isLineageMode: false })}
                                >
                                    <InfoCircleOutlined aria-hidden="true" />
                                    <span>{t('Details')}</span>
                                </Button>
                                <Button
                                    type="text"
                                    aria-label={t('Lineage')}
                                    className={`${isLineageMode ? 'f-btn-active' : ''} f-lineage-btn f-btn-flat`}
                                    onClick={() => navigateToLineageUrl({ location, history, isLineageMode: true })}
                                >
                                    <PartitionOutlined aria-hidden="true" />
                                    <span>{t('Lineage')}</span>
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                </Card>
            )}
        </>
    );
};
